import { Component, Element, Fragment, State, getAssetPath, h } from '@stencil/core';
import state from '../../store';
import { Business, Icon, Part } from '../../types';
import { getPartPrice, getProductPrice } from '../../utils/calc';
import { scrollToTop } from '../../utils/scroll';

@Component({
  tag: 'hemfixarna-product',
  styleUrl: 'hemfixarna-product.scss',
  shadow: false,
})
export class HemfixarnaProduct {
  @State() amount: number = 0;
  @State() hideDescription: boolean = true;
  @Element() private el: HTMLElement;

  addProduct() {
    const productInCart = state.cart.find(p => p.id === state.selectedProduct.ID);
    if (productInCart) {
      productInCart.amount++;
      state.cart = [...state.cart];
    } else {
      state.cart = [
        ...state.cart,
        {
          id: state.selectedProduct.ID,
          rut: state.selectedProduct.rut,
          rot: state.selectedProduct.rot,
          green: state.selectedProduct.green,
          amount: 1,
          parts: [],
          price: state.selectedProduct.price,
          name: state.selectedProduct.title,
          start_fee: !state.selectedProduct.hide_start_fee,
          terms_checkout: state.selectedProduct.terms_checkout,
          icon: state.selectedProduct.icon,
          start_fee_obj: state.selectedProduct.start_fee_obj,
        },
      ];
    }
  }

  removeProduct() {
    const productInCart = state.cart.find(p => p.id === state.selectedProduct.ID);
    if (productInCart && productInCart.amount > 1) {
      productInCart.amount--;
      state.cart = [...state.cart];
    } else {
      state.cart = state.cart.filter(p => p.id !== state.selectedProduct.ID);
    }
  }

  //Handle cart and parts
  addPart(part: Part) {
    const productInCart = state.cart.find(p => p.id === state.selectedProduct.ID);
    if (productInCart) {
      const partInCart = productInCart.parts.find(sp => sp.id === part.ID);
      if (partInCart) {
        partInCart.amount++;
        productInCart.parts = [...productInCart.parts];
      } else {
        productInCart.parts = [...productInCart.parts, { id: part.ID, amount: 1, price: part.price, name: part.title ?? part.title }];
      }
      state.cart = [...state.cart.filter(p => p.id !== productInCart.id), productInCart];
    }
  }

  removePart(part: Part) {
    const productInCart = state.cart.find(p => p.id === state.selectedProduct.ID);
    if (productInCart) {
      const partInCart = productInCart.parts.find(sp => sp.id === part.ID);
      if (partInCart && partInCart.amount > 1) {
        partInCart.amount--;
        productInCart.parts = [...productInCart.parts];
      } else {
        productInCart.parts = productInCart.parts.filter(sp => sp.id !== part.ID);
      }
      state.cart = [...state.cart.filter(p => p.id !== productInCart.id), productInCart];
    }
  }

  goToCart() {
    const productInCart = state.cart.find(p => p.id === state.selectedProduct.ID);
    if (productInCart) {
      state.step = 5;
      const el = this.el.closest('.hemfixarna_content');
      scrollToTop(el);
    }
  }

  getAmount() {
    return state.cart.find(p => p.id === state.selectedProduct.ID)?.amount || 0;
  }

  getPartAmount(partId: number) {
    const productInCart = state.cart.find(p => p.id === state.selectedProduct.ID);
    return productInCart?.parts.find(sp => sp.id === partId)?.amount || 0;
  }

  getTotalPrice() {
    let price = 0;
    const productInCart = state.cart.find(p => p.id === state.selectedProduct.ID);
    if (productInCart) {
      const partsPrice = productInCart.parts.reduce((acc, curr) => {
        const part = state.selectedProduct.parts.find(p => p.ID === curr.id);
        if (part) {
          return acc + part.price * curr.amount;
        }
        return acc;
      }, 0);
      price = state.selectedProduct.price * productInCart.amount + partsPrice;
    } else {
      price = state.selectedProduct.price;
    }
    return getProductPrice(state.selectedProduct, price);
  }

  componentDidRender() {
    const linkElem = document.createElement('link');
    linkElem.setAttribute('rel', 'stylesheet');
    linkElem.setAttribute('href', 'https://painting-frontend.vercel.app/static/css/main.css');
    const fonts = document.createElement('link');
    fonts.setAttribute('rel', 'stylesheet');
    fonts.setAttribute('href', 'https://painting-frontend.vercel.app/fonts.css');

    const checkAndInjectCSS = () => {
      const parents = this.el.getElementsByTagName('hemfixare-calculator');
      if (parents.length > 0 && parents[0].shadowRoot !== null) {
        parents[0].shadowRoot.appendChild(linkElem);
        parents[0].shadowRoot.appendChild(fonts);
      } else {
        setTimeout(checkAndInjectCSS, 1);
      }
    };

    checkAndInjectCSS();
  }

  render() {
    const checked = getAssetPath(`./assets/checked.svg`);
    const plus = getAssetPath(`./assets/plus.svg`);
    const minus = getAssetPath(`./assets/minus.svg`);

    if (state.maleri) {
      return (
        <div class="hemfixarna_painting">
          <script
            type="text/javascript"
            src={
              ['localhost', 'vercel'].some(v => window.location.href.includes(v))
                ? 'https://painting-dev.vercel.app/static/js/main.js'
                : 'https://painting-frontend.vercel.app/static/js/main.js'
            }
            defer
            async
          ></script>
          <hemfixare-calculator
            title={state.options.maleri_title}
            subtitle={state.options.maleri_text}
            modal="true"
            customer={state.business === Business.string ? 'string' : state.business}
          ></hemfixare-calculator>
        </div>
      );
    }

    return state.selectedProduct ? (
      <div class="hemfixarna_product">
        <div class="hemfixarna_product--top">
          {state.selectedProduct.icon && (
            <img width={80} src={(state.selectedProduct.icon as Icon).url ?? (state.selectedProduct.icon as string)} alt={state.selectedProduct.title} />
          )}
          <div>
            <h1>{state.selectedProduct.title}</h1>
            {!state.selectedProduct.invoice ? <h2>{getProductPrice(state.selectedProduct)} kr/st</h2> : <h2>{state.selectedProduct.invoice_price}</h2>}
          </div>
        </div>

        <div class="hemfixarna_product--grid">
          <div class="hemfixarna_product--left">
            {state.selectedProduct.list?.length && (
              <ul class="hemfixarna_features">
                {state.selectedProduct.list.map(l => (
                  <li key={l.bullet}>
                    <img src={checked} alt="checked" />
                    <p>{l.bullet}</p>
                  </li>
                ))}
              </ul>
            )}
            {state.selectedProduct.show_pdf && (
              <a
                href={`http://hemfixarna.se/wp-json/felix/generatepdf/${state.selectedProduct.ID}`}
                target="_blank"
                class="generate-pdf-button"
              >
                Ladda ner tjänstebeskrivning
              </a>
            )}
            {state.selectedProduct.description && (
              <p
                onClick={() => (this.hideDescription = false)}
                class={`hemfixarna_description ${this.hideDescription ? 'hemfixarna_description--hidden' : ''}`}
                innerHTML={state.selectedProduct.description}
              ></p>
            )}
          </div>
          <div class="hemfixarna_product--right">
            {state.selectedProduct.invoice ? (
              <hemfixarna-invoice />
            ) : (
              <Fragment>
                <ul>
                  <li class="hemfixarna_product--item">
                    <div>
                      <p>{state.selectedProduct.title}</p>
                      <p class="hemfixarna_product--price">{getProductPrice(state.selectedProduct)}kr/st</p>
                    </div>
                    <div class="hemfixarna_counter">
                      <img class={`${this.getAmount() === 0 ? 'disabled' : ''}`} src={minus} onClick={() => this.removeProduct()} />
                      <span>{this.getAmount()}</span>
                      <img src={plus} onClick={() => this.addProduct()} />
                    </div>
                  </li>
                  {state.selectedProduct.parts?.length &&
                    state.selectedProduct.parts.map(p => (
                      <li class="hemfixarna_part">
                        <div>
                          <p>{p.title ?? p.title}</p>
                          <p class="hemfixarna_product--price">{getPartPrice(p, state.selectedProduct)}kr/st</p>
                        </div>
                        <div class="hemfixarna_counter">
                          <img class={`${this.getPartAmount(p.ID) === 0 ? 'disabled' : ''}`} src={minus} onClick={() => this.removePart(p)} />
                          <span>{this.getPartAmount(p.ID)}</span>
                          <img class={`${this.getAmount() === 0 ? 'disabled' : ''}`} src={plus} onClick={() => this.addPart(p)} />
                        </div>
                      </li>
                    ))}
                </ul>
                <h4 class="hemfixarna_product--total">Totalt {this.getTotalPrice()} kr</h4>
                <button onClick={() => this.goToCart()} class={`hemfixarna_buy ${this.getAmount() === 0 ? 'disabled' : ''}`}>
                  Fortsätt
                </button>
              </Fragment>
            )}
            {!state.selectedProduct.hide_start_fee && state.selectedProduct.start_fee_obj?.title && (
              <p class="hemfixarna_terms">
                <strong>{state.selectedProduct.start_fee_obj.title}</strong>
                <br />
                <span innerHTML={state.selectedProduct.start_fee_obj.description}></span>
              </p>
            )}
            {state.options && <hemfixarna-info />}
          </div>
        </div>
      </div>
    ) : null;
  }
}
